import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from './ContactFormPopup';
export function SmsServices() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  return <>
      <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-441">
        <div className="relative z-10 mx-auto max-w-7xl" data-id="element-442">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="mb-12 sm:mb-16 text-center" data-id="element-443">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
            color: 'var(--text-primary)'
          }} data-id="element-444">
              {t('sms.pricing.title')}{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-445">
                {t('sms.pricing.titleHighlight')}
              </span>
            </h2>
            <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-446">
              {t('sms.pricing.subtitle')}
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-447">
            {/* Free Demo Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.1
          }} data-id="element-448">
              <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-449">
                <div className="flex items-center gap-3 mb-6" data-id="element-450">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-451">
                    <SparklesIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-452" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-453">
                    {t('pricing.demo.title')}
                  </h3>
                </div>

                <div className="mb-6" data-id="element-454">
                  <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff'
                }} data-id="element-455">
                    {t('pricing.demo.label')}
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-456">
                    {t('pricing.demo.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1" data-id="element-457">
                  {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-458">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-459">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-460" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-461">
                        {t(`pricing.demo.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-462">
                  {t('pricing.demo.cta')}
                </button>
              </div>
            </motion.div>

            {/* Website Subscription */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-463">
              <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-464">
                <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-465">
                  <div className="p-3 rounded-xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-466">
                    <RocketIcon className="w-6 h-6" style={{
                    color: 'var(--accent)'
                  }} data-id="element-467" />
                  </div>
                  <h3 className="text-xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-468">
                    {t('sms.pricing.launch.title')}
                  </h3>
                </div>

                <div className="mb-6 relative z-10" data-id="element-469">
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-470">
                    <span className="text-2xl font-bold line-through opacity-50" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-471">
                      $25
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-472">
                      {t('sms.pricing.launch.price')}
                    </span>
                    <span className="text-sm" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-473">
                      {t('sms.pricing.launch.period')}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base font-medium mt-2" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-474">
                    {t('sms.pricing.launch.desc')}
                  </p>
                </div>

                <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-475">
                  {[1, 2, 3, 4].map(i => <li key={i} className="flex items-start gap-3" data-id="element-476">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-477">
                        <CheckIcon className="w-3 h-3 text-green-500" data-id="element-478" />
                      </div>
                      <span style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-479">
                        {t(`pricing.launch.b${i}`)}
                      </span>
                    </li>)}
                </ul>

                <button onClick={() => setIsPopupOpen(true)} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-480">
                  {t('pricing.launch.cta')}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-481" />
    </>;
}